import { useEffect, useState } from 'react';
import { api, type Bench, type ModelInfo, type Quote, type Selection } from '../../lib/api';
import { chooseModel, defaultSelection, priceOf, readLast, writeLast } from '../../lib/models';

// ---------------------------------------------------------------------------
// What every spending tab holds (204): the model, the choices on it, and the
// server's quote for the press as it stands. One hook so four tabs cannot
// drift: the model starts on the browser's last choice, the choice resets
// when the model changes, the quote is asked again on every change with the
// price list standing in until it answers, and the last choice is remembered.
// ---------------------------------------------------------------------------

export function useBench(bench: Bench, models: ModelInfo[] | undefined, inputs: (sel: Selection) => Record<string, unknown> = () => ({}), units = 1) {
  const [model, setModelState] = useState<ModelInfo | null>(null);
  const [sel, setSel] = useState<Selection>({});
  const [quote, setQuote] = useState<Quote | null>(null);

  // The list arrives once; the choice settles once, from the browser's memory.
  useEffect(() => {
    if (!models || model) return;
    const m = chooseModel(models, readLast(bench));
    if (m) { setModelState(m); setSel(defaultSelection(m)); }
  }, [models, model, bench]);

  function setModel(m: ModelInfo) { setModelState(m); setSel(defaultSelection(m)); writeLast(bench, m.id); }

  // The server's quote for this press, asked again whenever the choice changes.
  const key = model ? `${model.id}|${JSON.stringify(sel)}|${JSON.stringify(inputs(sel))}` : '';
  useEffect(() => {
    if (!model) return;
    let live = true;
    setQuote(null);
    api.quote({ bench, model: model.id, inputs: { ...sel, ...inputs(sel) } })
      .then((q) => { if (live) setQuote(q); })
      .catch(() => undefined);
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const credits = quote ? quote.credits : priceOf(model, sel, units);
  return { model, setModel, sel, setSel, quote, credits };
}
